import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  // `cta-sheen` supplies the violet→fuchsia gradient *and* the hover sweep, so
  // there is no `bg-*` here to conflict with it. Every stop of `--gradient-cta`
  // is picked to clear 4.5:1 against white — the fuchsia end is the tightest at
  // 4.71:1 — which is why that token stays at the 600 level in both modes.
  primary: 'cta-sheen text-white shadow-glow hover:shadow-lifted',
  secondary:
    'border border-line bg-card text-strong hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:border-brand-500/50 dark:hover:bg-brand-950 dark:hover:text-brand-100',
  ghost: 'text-muted hover:bg-sunken hover:text-strong',
  danger:
    'border border-critical-200 bg-critical-50 text-critical-700 hover:bg-critical-100 dark:border-critical-600 dark:bg-critical-950 dark:text-critical-100 dark:hover:bg-critical-700/40',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 gap-1.5 px-3.5 text-sm',
  md: 'h-10 gap-2 px-4.5 text-sm',
  lg: 'h-12 gap-2.5 px-6 text-base',
};

const BASE = cn(
  // Pill rather than a rounded rectangle: it is the single biggest cue that this
  // is a consumer app and not an enterprise dashboard.
  'inline-flex shrink-0 items-center justify-center rounded-pill font-semibold no-underline',
  'transition-[background-color,border-color,color,box-shadow,transform] duration-150',
  // A press that gives slightly under the finger. `transform` only, so it costs
  // no layout, and the global reduced-motion rule flattens it to nothing.
  'active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50'
);

/** Decorative icon slot: hidden from the a11y tree, since the label carries it. */
function Icon({ children }: { children: ReactNode }) {
  return (
    <span aria-hidden="true" className="grid shrink-0 place-items-center [&>svg]:size-4">
      {children}
    </span>
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
}

export function Button({
  className,
  variant = 'primary',
  size = 'md',
  icon,
  children,
  // Defaulting to `button` rather than `submit`: an icon button inside the
  // profile form must not submit it by accident.
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button type={type} className={cn(BASE, VARIANTS[variant], SIZES[size], className)} {...props}>
      {icon ? <Icon>{icon}</Icon> : null}
      {children}
    </button>
  );
}

export interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
}

/**
 * A link that looks like a button. A separate component rather than an `as` prop
 * so the anchor keeps its own semantics — middle-click, open-in-new-tab, and the
 * correct screen-reader role all keep working.
 */
export function ButtonLink({
  className,
  variant = 'secondary',
  size = 'md',
  icon,
  children,
  ...props
}: ButtonLinkProps) {
  return (
    <a className={cn(BASE, VARIANTS[variant], SIZES[size], className)} {...props}>
      {icon ? <Icon>{icon}</Icon> : null}
      {children}
    </a>
  );
}
